/**
 * HeroCarousel - A class for creating a continuous stream of images
 * flowing from right to left with staggered entries
 * Requires GSAP library
 */
class HeroCarousel {
    /**
     * @param {Object} options - Configuration options
     * @param {string} options.containerSelector - Selector for the carousel container
     * @param {string} options.itemSelector - Selector for the individual carousel items
     * @param {number} options.baseDuration - Base duration for moving across screen (default: 25)
     * @param {number} options.speedVariation - How much to vary speed between items (default: 2)
     * @param {number} options.fadeInDuration - Duration of fade in effect in seconds (default: 1)
     * @param {number} options.fadeOutDuration - Duration of fade out effect in seconds (default: 1)
     * @param {number} options.initialStagger - Stagger time for initial appearance in seconds (default: 0.5)
     * @param {number} options.flowDelay - Time between each image in the flow in seconds (default: 3)
     * @param {boolean} options.autoInit - Whether to initialize automatically (default: true)
     * @param {number} options.minWidth - Minimum width percentage for images (default: 25)
     * @param {number} options.maxWidth - Maximum width percentage for images (default: 35)
     * @param {number} options.minBottomOffset - Minimum bottom offset percentage (default: -10)
     * @param {number} options.maxBottomOffset - Maximum bottom offset percentage (default: 80)
     * @param {number} options.rightEntryMin - Minimum right offset percentage for entry (default: 60)
     * @param {number} options.rightEntryMax - Maximum right offset percentage for entry (default: 80)
     * @param {number} options.initialDelay - Delay before starting animation (default: 0)
     * @param {number} options.flowStartDelay - Delay before starting the continuous flow (default: 3)
     * @param {number} options.initialItems - Number of items to initialize with fixed positions (default: 5)
     */
    constructor(options = {}) {
      // Base defaults that don't change with viewport
      this.baseDefaults = {
        containerSelector: '.c-carousel_inner',
        itemSelector: '.c-carousel_item',
        baseDuration: 25,
        speedVariation: 4,
        fadeInDuration: 1,
        fadeOutDuration: 1,
        initialStagger: 0.25,
        flowDelay: 5,
        autoInit: true,
        minWidth: 20,
        maxWidth: 30,
        minBottomOffset: -5,
        maxBottomOffset: 80,
        bottomOffsetLimit: 25,
        rightEntryMin: 75,
        rightEntryMax: 80,
        initialDelay: 0,
        flowStartDelay: 9,
        initialItems: 5
      };

      // Set initial defaults based on viewport
      this.defaults = { ...this.baseDefaults };
      this.updateDefaultsForViewport();

      // Merge options with defaults
      this.options = { ...this.defaults, ...options };
  
      // Initialize state
      this.container = null;
      this.items = [];
      this.initialTimelines = [];  // Timelines for initial items
      this.masterFlowTimeline = null; // Master timeline for the continuous flow
      this.initialized = false;
      this.viewportWidth = 0;
      this.viewportHeight = 0;
      this.usedBottomPositions = [];
      this.positionCount = 0;
  
      // Add viewport state tracking
      this.lastViewportWidth = window.innerWidth;
      this.isMobile = this.lastViewportWidth < 991;
      this.isSmallMobile = this.lastViewportWidth < 768;
  
      // Auto initialization
      if (this.options.autoInit && document.readyState === 'complete') {
        this.init();
      } else if (this.options.autoInit) {
        document.addEventListener('DOMContentLoaded', () => this.init());
      }
    }
  
    /**
     * Update defaults based on current viewport width
     */
    updateDefaultsForViewport() {
      const viewportWidth = window.innerWidth;
      
      // Reset to base defaults first
      this.defaults = { ...this.baseDefaults };

      if (viewportWidth < 991) {
        this.defaults.minWidth = 35;
        this.defaults.maxWidth = 45;
      }

      if (viewportWidth < 768) {
        this.defaults.minWidth = 45;
        this.defaults.maxWidth = 60;
        this.defaults.flowDelay = 4;
        this.defaults.flowStartDelay = 4;
        this.defaults.baseDuration = 20;
        this.defaults.speedVariation = 3;
        this.defaults.initialStagger = 0.15;
      }
    }
  
    /**
     * Initialize the carousel
     */
    init() {
      // Update defaults based on current viewport
      this.updateDefaultsForViewport();
      // Update options with new defaults
      this.options = { ...this.defaults, ...this.options };

      this.container = document.querySelector(this.options.containerSelector);
      
      if (!this.container) {
        console.warn('HeroCarousel: Container not found');
        return;
      }
  
      // Make sure GSAP is available
      if (typeof gsap === 'undefined') {
        console.error('HeroCarousel: GSAP library is required');
        return;
      }
  
      // Store items
      this.items = Array.from(document.querySelectorAll(this.options.itemSelector));
      
      if (this.items.length === 0) {
        console.warn('HeroCarousel: No items found');
        return;
      }
      
      // Update viewport dimensions
      this.updateViewportDimensions();
  
      // Set up resize handler
      this.setupResizeHandler();
      
      // Reset all items to be invisible
      this.items.forEach(item => {
        gsap.set(item, { autoAlpha: 0 });
      });
      
      // Start the animation
      setTimeout(() => {
        this.startAnimation();
      }, 100);
  
      // Get logo elements
      this.heroLogo = document.querySelector('.c-logo_container .e-hero_logo_svg');
      this.navLogo = document.querySelector('.c-nav_topbar_cont .e-nav_logo_link svg');
      
      if (this.heroLogo && this.navLogo) {
        this.setupLogoAnimation();
      }
  
      this.initialized = true;
      return this;
    }
  
    /**
     * Update viewport dimensions
     */
    updateViewportDimensions() {
      this.viewportWidth = window.innerWidth;
      this.viewportHeight = this.container.clientHeight;
    }
  
    /**
     * Generate a random width percentage between min and max
     */
    getRandomWidth() {
      return this.options.minWidth + Math.random() * (this.options.maxWidth - this.options.minWidth);
    }
    
    /**
     * Get a random bottom position that doesn't overlap with recent positions
     */
    getRandomBottomPosition() {
      let rows;
      
      if (this.viewportWidth < 768) {
        rows = {
          top: {
            min: 54,    // Start of top row
            max: 80,    // End of top row
            buffer: 10
          },
          middle: {
            min: 26,    // Start of middle row
            max: 54,    // End of middle row
            buffer: 10
          },
          bottom: {
            min: 0,     // Start of bottom row
            max: 26,    // End of bottom row
            buffer: 10
          }
        };
      } else {
        rows = {
          top: {
            min: 55,    // Start of top row
            max: 80,    // End of top row
            buffer: 10
          },
          middle: {
            min: 25,  // Start of middle row
            max: 55,    // End of middle row
            buffer: 10
          },
          bottom: {
            min: -5,    // Start of bottom row
            max: 25,  // End of bottom row
            buffer: 10
          }
        };
      }

      // Keep track of last few positions (e.g., last 3)
      const lastPosition = this.usedBottomPositions[this.usedBottomPositions.length - 1];
      let newBottom;

      if (lastPosition === undefined) {
        // First element logic remains the same
        newBottom = rows.bottom.min + rows.bottom.buffer + 
          Math.random() * (rows.bottom.max - rows.bottom.min - rows.bottom.buffer * 2);

        if(this.viewportWidth < 768){
          newBottom = rows.middle.min + rows.middle.buffer + 
            Math.random() * (rows.middle.max - rows.middle.min - rows.middle.buffer * 2);
        }
      } else {
        // Get the row of the last position
        let lastRow;
        if (lastPosition >= rows.middle.max) {
          lastRow = 'top';
        } else if (lastPosition >= rows.bottom.max) {
          lastRow = 'middle';
        } else {
          lastRow = 'bottom';
        }

        // Force alternating rows
        switch(lastRow) {
          case 'top':
            newBottom = rows.bottom.min + rows.bottom.buffer + 
              Math.random() * (rows.bottom.max - rows.bottom.min - rows.bottom.buffer * 2);
            break;
          case 'middle':
            newBottom = rows.top.min + rows.top.buffer + 
              Math.random() * (rows.top.max - rows.top.min - rows.top.buffer * 2);
            break;
          case 'bottom':
            newBottom = rows.middle.min + rows.middle.buffer + 
              Math.random() * (rows.middle.max - rows.middle.min - rows.middle.buffer * 2);
            break;
        }
      }

      // Keep last few positions (maintain a history of 3)
      this.usedBottomPositions.push(newBottom);
      if (this.usedBottomPositions.length > 3) {
        this.usedBottomPositions.shift(); // Remove oldest position
      }

      return newBottom;
    }
  
    /**
     * Get random entry position from right side
     */
    getRandomRightEntryPosition() {
      return this.options.rightEntryMin + 
        Math.random() * (this.options.rightEntryMax - this.options.rightEntryMin);
    }
  
    /**
     * Start the animation sequence
     */
    startAnimation() {
      // Create master timeline for intro animations
      const introTimeline = gsap.timeline();

      gsap.set('.c-home_hero_title', {
        autoAlpha: 0,
        y: 20
      });

      // Animate hero title
      introTimeline.to('.c-home_hero_title', {
        duration: 2,
        autoAlpha: 1,
        y: 0,
        ease: "power3.out"
      });

      // Staggered animation for logo paths
      introTimeline.fromTo('[id^="1"], [id^="2"], [id^="3"], [id^="4"], [id^="5"], [id^="6"]', {
        autoAlpha: 0,
        y: 20
      }, {
        duration: 2,
        autoAlpha: 1,
        y: 0,
        stagger: 0.1,
        ease: "power2.out"
      }, "-=80%");

      // Split into initial items and flow items
      const initialItems = this.items.slice(0, this.options.initialItems);

      // Start carousel animations 60% before the intro timeline ends
      introTimeline.call(() => {
        this.animateInitialItems(initialItems);
        
        this.flowSetupTimeout = setTimeout(() => {
          this.setupFlowTimeline();
        }, (this.options.initialDelay + this.options.flowStartDelay) * 1000);
      }, [], "-=1.5");
    }
  
    /**
     * Animate the initial set of items (first 5) that have CSS-defined positions
     * @param {HTMLElement[]} items - Initial items to animate
     */
    animateInitialItems(items) {
      items.forEach((item, index) => {
        // Create a timeline for this item (no repeat)
        const tl = gsap.timeline({
          onComplete: () => {
            // Remove this timeline from initialTimelines array
            const timelineIndex = this.initialTimelines.findIndex(t => t.element === item);
            if (timelineIndex > -1) {
              this.initialTimelines.splice(timelineIndex, 1);
            }
            // Kill the timeline
            tl.kill();
          }
        });
        
        // Get the movement duration upfront (same as flow items)
        const moveDuration = this.getRandomDuration();

        // get current item width
        const itemWidth = item.offsetWidth;
        
        // Fade in at the current CSS-defined position
        tl.to(item, {
          autoAlpha: 1,
          duration: this.options.fadeInDuration,
          ease: "power1.inOut",
          delay: this.options.initialDelay + (index * this.options.initialStagger)
        });
        
        // After fade-in is complete, start moving left
        if(this.viewportWidth < 768){
          tl.to(item, {
            x: `-=${this.viewportWidth + itemWidth}`,
            duration: moveDuration,
            ease: "none",
            delay: 0.25
          }, "<");
        } else {
          tl.to(item, {
            x: `-=${this.viewportWidth - itemWidth}`,
            duration: moveDuration,
            ease: "none",
            delay: 0.25
          }, "<");
        }
        // Fade out as it exits the left side
        tl.to(item, {
          autoAlpha: 0,
          duration: this.options.fadeOutDuration,
          ease: "power1.inOut"
        }, "<+" + (moveDuration - this.options.fadeOutDuration));
        
        // Add this timeline to our initial timelines collection
        this.initialTimelines.push({ element: item, timeline: tl });
      });
    }
  
    /**
     * Setup the master flow timeline that contains all items in sequence
     */
    setupFlowTimeline() {
      // Create the master timeline (no repeat needed)
      this.masterFlowTimeline = gsap.timeline();
      
      // Restructure items array: move initial items to the end
      const initialItems = this.items.slice(0, this.options.initialItems);
      const otherItems = this.items.slice(this.options.initialItems);
      const reorderedItems = [...otherItems, ...initialItems];
      
      // Track the last start time
      this.lastStartTime = 0;
      
      // Initialize the flow with staggered items
      reorderedItems.forEach((item) => {
        this.addItemToFlow(item);
      });
    }
  
    /**
     * Add an item to the flow timeline
     * @param {HTMLElement} item - The item to animate
     */
    addItemToFlow(item) {
      const itemWidth = this.getRandomWidth();

      // Create a single animation sequence for this item
      const tl = gsap.timeline({
        onComplete: () => {
          // When animation completes, add it back to the flow
          this.addItemToFlow(item);
          // Remove this timeline from the master timeline
          tl.kill();
        }
      });
      
      // Setup initial position and styling
      tl.set(item, {
        autoAlpha: 0,
        x: 0,
        bottom: `${this.getRandomBottomPosition()}%`,
        left: `${this.getRandomRightEntryPosition()}%`,
        width: `${itemWidth}%`,
        top: 'unset'
      });

      // get item pixel width
      const itemPixelWidth = item.offsetWidth;
      
      // Fade in
      tl.to(item, {
        autoAlpha: 1,
        duration: this.options.fadeInDuration,
        ease: "power1.inOut"
      });
      
      // Move left
      const moveDuration = this.getRandomDuration();
      if(this.viewportWidth < 768){
        tl.to(item, {
          x: `-=${this.viewportWidth + itemPixelWidth}`,
          duration: moveDuration,
          ease: "none"
        }, "<");
      } else {
        tl.to(item, {
          x: `-=${this.viewportWidth - itemWidth}`,
          duration: moveDuration,
          ease: "none"
        }, "<");
      }
      
      // Fade out
      tl.to(item, {
        autoAlpha: 0,
        duration: this.options.fadeOutDuration,
        ease: "power1.inOut"
      }, "<+" + (moveDuration - this.options.fadeOutDuration));
      
      // Add this sequence to the master timeline at the next available start time
      this.masterFlowTimeline.add(tl, this.lastStartTime);
      
      // Update the last start time for the next item
      this.lastStartTime += this.options.flowDelay;
    }
  
    /**
     * Get a randomized duration based on base duration and speed variation
     */
    getRandomDuration() {
        // return this.options.baseDuration;
      return this.options.baseDuration - (Math.random() * this.options.speedVariation);
    }
  
    /**
     * Set up the resize handler
     */
    setupResizeHandler() {
      const resizeHandler = () => {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
          const currentWidth = window.innerWidth;
          const wasMobile = this.isMobile;
          const wasSmallMobile = this.isSmallMobile;
          
          // Check if we've crossed any breakpoints
          const isMobileNow = currentWidth < 991;
          const isSmallMobile = currentWidth < 768;
          
          // If we've crossed a breakpoint
          if (wasMobile !== isMobileNow || wasSmallMobile !== isSmallMobile) {
            // Store current user options before destroying
            const userOptions = {};
            // Only store options that differ from current defaults
            Object.keys(this.options).forEach(key => {
              if (this.options[key] !== this.defaults[key]) {
                userOptions[key] = this.options[key];
              }
            });

            // Kill all existing animations
            this.destroy();
            
            // Update viewport state
            this.lastViewportWidth = currentWidth;
            this.isMobile = isMobileNow;
            this.isSmallMobile = isSmallMobile;
            
            // Reset position tracking
            this.usedBottomPositions = [];
            this.positionCount = 0;
            this.lastStartTime = 0;
            
            // Update defaults for new viewport size
            this.updateDefaultsForViewport();
            
            // Merge stored user options with new defaults
            this.options = { ...this.defaults, ...userOptions };
            
            // Reinitialize and start fresh animation cycle
            this.init();
          }
        }, 250);
      };
      
      window.addEventListener('resize', resizeHandler);
      this.resizeHandler = resizeHandler; // Store for cleanup
    }
  
    /**
     * Pause all animations
     */
    pause() {
      // Pause all initial timelines
      this.initialTimelines.forEach(({timeline}) => {
        timeline.pause();
      });
      
      // Pause master flow timeline
      if (this.masterFlowTimeline) {
        this.masterFlowTimeline.pause();
      }
      
      return this;
    }
  
    /**
     * Resume all animations
     */
    resume() {
      // Resume all initial timelines
      this.initialTimelines.forEach(({timeline}) => {
        timeline.play();
      });
      
      // Resume master flow timeline
      if (this.masterFlowTimeline) {
        this.masterFlowTimeline.play();
      }
      
      return this;
    }
  
    /**
     * Destroy the carousel and clean up
     */
    destroy() {
      // Kill any pending flow timeline setup
      if (this.flowSetupTimeout) {
        clearTimeout(this.flowSetupTimeout);
        this.flowSetupTimeout = null;
      }

      // Kill all initial timelines
      this.initialTimelines.forEach(({timeline, element}) => {
        timeline.kill();
        gsap.killTweensOf(element); // Kill any lingering tweens on the element
      });
      
      // Kill master flow timeline and any individual item timelines
      if (this.masterFlowTimeline) {
        // Kill all child timelines first
        this.masterFlowTimeline.getChildren().forEach(child => {
          child.kill();
        });
        this.masterFlowTimeline.kill();
        this.masterFlowTimeline = null;
      }
      
      // Clear timelines array
      this.initialTimelines = [];
      
      // Reset all items and kill any lingering tweens
      this.items.forEach(item => {
        gsap.killTweensOf(item); // Kill any remaining tweens
        gsap.set(item, { 
          clearProps: "all",
          autoAlpha: 0 // Ensure items are hidden
        });
      });
      
      // Reset all tracking variables
      this.usedBottomPositions = [];
      this.positionCount = 0;
      this.lastStartTime = 0;
      
      // Remove resize listener
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler);
      }
      
      // Kill ScrollTrigger
      if (this.logoScrollTrigger) {
        this.logoScrollTrigger.kill();
      }
      
      // Reset logo positions
      if (this.heroLogo) {
        gsap.set(this.heroLogo, {
          clearProps: 'all'
        });
      }
      if (this.navLogo) {
        gsap.set(this.navLogo, {
          clearProps: 'all'
        });
      }
      
      this.initialized = false;
      return this;
    }
  
    /**
     * Update carousel options
     * @param {Object} newOptions - New options to apply
     */
    updateOptions(newOptions = {}) {
      this.options = { ...this.options, ...newOptions };
      if (this.initialized) {
        this.destroy();
        this.init();
      }
      return this;
    }

    setupLogoAnimation() {
      // Get initial positions and dimensions
      const heroRect = this.heroLogo.getBoundingClientRect();
      const navRect = this.navLogo.getBoundingClientRect();

      const heroLogoCont = document.querySelector('.c-logo_container');
      const heroLogoContRect = heroLogoCont.getBoundingClientRect();

      const heroCont = document.querySelector('.c-section_home_hero');
      const heroContRect = heroCont.getBoundingClientRect();

      const heroLogoYOffset = heroRect.top;
      const heroLogoXOffset = heroRect.left;
      const heroLogoWidth = heroRect.width;
      const heroLogoHeight = heroRect.height;
      const navLogoYOffset = navRect.top;
      const navLogoXOffset = navRect.left;
      const navLogoWidth = navRect.width;
      const navLogoHeight = navRect.height;

      // Hide nav logo initially
      gsap.set(this.navLogo, { opacity: 0 });

      // Create the GSAP animation for just the hero logo transformation
      const logoTween = gsap.fromTo(this.heroLogo, 
          {
          width: heroLogoWidth,
          height: heroLogoHeight,
          }, 
          {
          width: navLogoWidth,
          height: navLogoHeight,
          x: navLogoXOffset - heroLogoXOffset,
          duration: 1,
          }
      );

      // Calculate the start position based on viewport width
      const isMobile = window.innerWidth < 768;
      const startOffset = isMobile ? heroContRect.height - 150 : heroContRect.height;

      // Then link it to the ScrollTrigger
      this.logoScrollTrigger = ScrollTrigger.create({
          trigger: this.heroLogo,
          start: `bottom top+=${startOffset}`,
          end: `bottom top+=${navRect.top + navRect.height}`,
          animation: logoTween,
          scrub: true,
          markers: false,
          onEnter: () => {
              // Hide nav logo initially
              gsap.set(this.navLogo, { opacity: 0 });
          },
          onLeave: () => {
              // When scrolling past the end, switch logos
              gsap.set(this.navLogo, { opacity: 1 });
              gsap.set(this.heroLogo, { opacity: 0 });
          },
          onEnterBack: () => {
              // When scrolling back up into the animation range
              gsap.set(this.navLogo, { opacity: 0 });
              gsap.set(this.heroLogo, { opacity: 1 });
          }
      });
    }
  }
  
  // Export the class
  export default HeroCarousel;